import { existsSync } from "node:fs";
import { mkdir, readFile, readdir } from "node:fs/promises";
import { join } from "node:path";
import { parseFrontmatter, exclusiveCreateFile } from "@vortex-os/core";
import {
  recordAcceptance,
  recordDecline,
} from "./decline-store.js";
import { computeFingerprint } from "./fingerprint.js";
import type { ExpectedShape, LLMJudge } from "./llm-judge.js";
import type {
  AcceptResult,
  Proposal,
  ProposalAction,
  Proposer,
  ProposerContext,
} from "./proposer.js";

/**
 * Hub auto-curation surface — Sub-phase b.
 *
 * Walks the configured content categories (default: `worklog/`,
 * `decision-log/`, `runbooks/`), groups documents by candidate topic, and
 * proposes a `_HUB-<topic>.md` cross-link page when the cluster crosses a
 * threshold *and* the LLM's coherence judgment agrees.
 *
 * Threshold ladder:
 *
 *  - **Below `weakSignalAt`** (default 3) → no LLM call, no proposal. A
 *    one- or two-doc topic does not deserve a hub.
 *  - **Weak signal (`weakSignalAt` ≤ count < `strongSignalAt`)** → LLM gate
 *    runs in **propose-veto** mode. The LLM must affirm the cluster has
 *    real cross-cutting cohesion; default is suppress. This avoids hubs
 *    over thin coincidences (three docs that share a word but not a topic).
 *  - **Strong signal (count ≥ `strongSignalAt`, default 5)** → LLM gate
 *    runs in **suppress-veto** mode. The LLM must actively reject the
 *    cluster; default is propose. Quantitative accumulation alone is
 *    enough to surface a candidate, but the LLM can still hide nonsense
 *    clusters.
 *
 * The asymmetry is the design's core safeguard: thresholds catch what the
 * LLM might miss, and the LLM catches what counts miss. Neither alone is
 * sufficient.
 *
 * `action.kind` is always `create-file` — hubs live flat under `hubs/` by
 * convention. There is no folder-creation decision for the hub surface.
 *
 * If a hub at the proposed path already exists, the proposer returns
 * `null` — the hub is the user's to curate after that point; the proposer
 * does not auto-update existing hubs.
 */

const DEFAULT_CATEGORIES: readonly string[] = [
  "worklog",
  "decision-log",
  "runbooks",
];
const HUB_DIR = "hubs";
const MIN_KEYWORD_LENGTH = 3;
const MAX_LLM_DOC_LIST = 30;

export interface HubProposerOptions {
  /** Threshold for triggering propose-veto LLM gate. Below this, no proposal. Default 3. */
  readonly weakSignalAt: number;
  /** Threshold for switching to suppress-veto LLM gate. Default 5. */
  readonly strongSignalAt: number;
  /**
   * Content categories (data-relative) to scan for topic clusters. Default
   * `worklog`, `decision-log`, `runbooks`. Hub itself, `_memory/`, and the
   * curator's own `_proactive-curator/` are never scanned (a hub-of-hubs
   * is out of scope; memory is not topical content).
   */
  readonly scanCategories?: readonly string[];
  /** Decline expiry in days. Default 30. */
  readonly declineExpiryDays?: number;
}

export interface HubInput {
  /**
   * Hub evaluation has no per-call input beyond context — the filesystem is
   * the input. This shape is kept for future extension (e.g. a hint to scan
   * only a specific category) without breaking the `Proposer<Input, P>`
   * contract.
   */
  readonly hint?: { readonly onlyCategories?: readonly string[] };
}

export interface HubProposal extends Proposal {
  readonly kind: "create-hub";
}

interface DocRecord {
  /** data-relative path, POSIX-slash. */
  readonly path: string;
  readonly filename: string;
  readonly frontmatterTopic?: string;
  readonly tags: readonly string[];
  readonly filenameKeywords: readonly string[];
}

interface TopicCluster {
  readonly topic: string;
  readonly docs: readonly DocRecord[];
}

interface HubCoherenceVerdict {
  readonly verdict: "yes" | "no";
  readonly contextParagraph?: string;
}

export class HubProposer implements Proposer<HubInput, HubProposal> {
  readonly kind = "create-hub";

  constructor(private readonly options: HubProposerOptions) {}

  async evaluate(
    input: HubInput,
    ctx: ProposerContext,
  ): Promise<HubProposal | null> {
    const categories = input.hint?.onlyCategories
      ?? this.options.scanCategories
      ?? DEFAULT_CATEGORIES;

    // 1. Scan + group.
    const docs = await scanDocs(ctx.cwd, categories);
    if (docs.length === 0) return null;
    const clusters = groupByTopic(docs);

    // 2. Pick the best cluster — the largest above the weak threshold whose
    // proposed hub does not already exist. We surface at most one hub
    // proposal per evaluate() call; the host can re-call to surface more.
    const candidate = pickCluster(clusters, this.options.weakSignalAt, ctx.cwd);
    if (!candidate) return null;

    // 3. LLM gate (asymmetric).
    const verdict = await askCoherence(
      ctx.llm,
      candidate,
      candidate.docs.length >= this.options.strongSignalAt,
    );
    if (!verdict) return null;

    // 4. Build the proposal.
    const slug = candidate.topic;
    const filename = `_HUB-${slug}.md`;
    const action: ProposalAction = {
      kind: "create-file",
      folderPath: HUB_DIR,
      filename,
      body: renderHubBody(candidate, verdict.contextParagraph, ctx.now),
    };
    const targetPath = `${HUB_DIR}/${filename}`;

    // 5. Decline check.
    const sourceDocs = candidate.docs.map((d) => d.path);
    const fingerprint = computeFingerprint({
      kind: "create-hub",
      topic: candidate.topic,
      sourceDocs,
      actionKind: action.kind,
      targetPath,
    });
    if (ctx.declinedFingerprints.has(fingerprint)) return null;

    // 6. Emit.
    const rationale = candidate.docs.length >= this.options.strongSignalAt
      ? `${candidate.docs.length} docs about "${candidate.topic}" — strong-signal cluster (above ${this.options.strongSignalAt}); LLM concurred.`
      : `${candidate.docs.length} docs about "${candidate.topic}" — weak-signal cluster (≥ ${this.options.weakSignalAt}); LLM affirmed cohesion.`;
    const declineExpiryDays = this.options.declineExpiryDays;

    return {
      kind: "create-hub",
      fingerprint,
      action,
      preview: renderPreview(candidate, verdict.contextParagraph),
      rationale,
      sourceRefs: sourceDocs,
      onAccept: async () => {
        const writtenPath = await applyHubCreate(ctx.cwd, action);
        await recordAcceptance(ctx.cwd, {
          kind: "create-hub",
          fingerprint,
          topic: candidate.topic,
          actionKind: action.kind,
          writtenPath,
          now: ctx.now,
        });
        return {
          writtenPath,
          actionApplied: action.kind,
          nextActionHint: `Hub created at hubs/${filename}. Open it to review the cross-links; the source docs were not modified.`,
        } satisfies AcceptResult;
      },
      onDecline: async () => {
        await recordDecline(ctx.cwd, {
          kind: "create-hub",
          fingerprint,
          topic: candidate.topic,
          actionKind: action.kind,
          targetPath,
          sourceDocs,
          now: ctx.now,
          ...(declineExpiryDays !== undefined ? { expiryDays: declineExpiryDays } : {}),
        });
      },
    };
  }
}

async function scanDocs(
  cwd: string,
  categories: readonly string[],
): Promise<DocRecord[]> {
  const dataDir = join(cwd, "data");
  if (!existsSync(dataDir)) return [];
  const out: DocRecord[] = [];
  for (const category of categories) {
    const abs = join(dataDir, category);
    if (!existsSync(abs)) continue;
    await walk(abs, category, out);
  }
  return out;
}

async function walk(
  absDir: string,
  relPath: string,
  acc: DocRecord[],
): Promise<void> {
  let entries;
  try {
    entries = await readdir(absDir, { withFileTypes: true });
  } catch {
    return;
  }
  for (const e of entries) {
    if (e.isDirectory()) {
      if (e.name.startsWith(".") || e.name.startsWith("_")) continue;
      await walk(join(absDir, e.name), `${relPath}/${e.name}`, acc);
    } else if (e.isFile() && e.name.endsWith(".md")) {
      if (e.name === "README.md" || e.name === "_INDEX.md" || e.name === "MEMORY.md") continue;
      if (e.name.startsWith("_TEMPLATE")) continue;
      const filePath = join(absDir, e.name);
      let frontmatterTopic: string | undefined;
      let tags: string[] = [];
      try {
        const raw = await readFile(filePath, "utf8");
        const parsed = parseFrontmatter<Record<string, unknown>>(raw);
        if (typeof parsed.frontmatter.topic === "string") {
          frontmatterTopic = parsed.frontmatter.topic.trim();
        }
        if (Array.isArray(parsed.frontmatter.tags)) {
          tags = parsed.frontmatter.tags.filter((t): t is string => typeof t === "string");
        }
      } catch {
        // unreadable or no frontmatter — leave fields empty
      }
      acc.push({
        path: `${relPath}/${e.name}`,
        filename: e.name,
        ...(frontmatterTopic ? { frontmatterTopic } : {}),
        tags,
        filenameKeywords: extractFilenameKeywords(e.name),
      });
    }
  }
}

function extractFilenameKeywords(filename: string): string[] {
  const stem = filename.replace(/\.md$/i, "");
  const withoutDate = stem.replace(/^\d{4}-\d{2}-\d{2}(?:_\d{4})?-/, "");
  return withoutDate
    .split(/[-_\s]+/)
    .map((s) => s.toLowerCase())
    .filter((s) => s.length >= MIN_KEYWORD_LENGTH);
}

function groupByTopic(docs: readonly DocRecord[]): TopicCluster[] {
  const buckets = new Map<string, DocRecord[]>();
  for (const doc of docs) {
    const topics = candidateTopicsFor(doc);
    for (const topic of topics) {
      const list = buckets.get(topic);
      if (list) {
        if (!list.some((d) => d.path === doc.path)) list.push(doc);
      } else {
        buckets.set(topic, [doc]);
      }
    }
  }
  const clusters: TopicCluster[] = [];
  for (const [topic, list] of buckets) {
    clusters.push({ topic, docs: list });
  }
  // Largest cluster first so pickCluster() finds the best candidate quickly.
  clusters.sort((a, b) => b.docs.length - a.docs.length);
  return clusters;
}

function candidateTopicsFor(doc: DocRecord): string[] {
  const topics = new Set<string>();
  if (doc.frontmatterTopic) topics.add(slugify(doc.frontmatterTopic));
  for (const tag of doc.tags) {
    const slug = slugify(tag);
    if (slug.length >= MIN_KEYWORD_LENGTH) topics.add(slug);
  }
  for (const kw of doc.filenameKeywords) {
    topics.add(kw);
  }
  return [...topics];
}

function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, "-");
}

function pickCluster(
  clusters: readonly TopicCluster[],
  weakThreshold: number,
  cwd: string,
): TopicCluster | null {
  for (const c of clusters) {
    if (c.docs.length < weakThreshold) return null; // sorted desc — first under-threshold means done
    const hubPath = join(cwd, "data", HUB_DIR, `_HUB-${c.topic}.md`);
    if (existsSync(hubPath)) continue;
    return c;
  }
  return null;
}

async function askCoherence(
  llm: LLMJudge,
  cluster: TopicCluster,
  isStrongSignal: boolean,
): Promise<HubCoherenceVerdict | null> {
  const prompt = renderCoherencePrompt(cluster, isStrongSignal);
  const expected: ExpectedShape = {
    shape: "json",
    schema: {
      verdict: "yes|no",
      contextParagraph: "string (1-2 sentences, used in the hub body when verdict=yes)",
    },
  };
  const raw = await llm.ask(prompt, expected);
  return interpretCoherence(raw, isStrongSignal);
}

function renderCoherencePrompt(
  cluster: TopicCluster,
  isStrongSignal: boolean,
): string {
  const docList = cluster.docs
    .slice(0, MAX_LLM_DOC_LIST)
    .map((d) => `  - ${d.path}`)
    .join("\n");
  const truncationNote = cluster.docs.length > MAX_LLM_DOC_LIST
    ? `\n  (... ${cluster.docs.length - MAX_LLM_DOC_LIST} more docs omitted for brevity)`
    : "";
  if (isStrongSignal) {
    return [
      `${cluster.docs.length} documents share the candidate topic "${cluster.topic}" — a strong-signal cluster.`,
      "",
      "Documents:",
      docList + truncationNote,
      "",
      "Default action is to propose creating a `_HUB-` page that cross-links them. Reject *only* if the docs are clearly incoherent — sharing a keyword but not a real subject. If the cluster has any reasonable cohesion, return verdict=yes.",
      "",
      `Return JSON: { "verdict": "yes" | "no", "contextParagraph": "1-2 sentences describing the topic for the hub body (required if verdict=yes)" }`,
    ].join("\n");
  }
  return [
    `${cluster.docs.length} documents share the candidate topic "${cluster.topic}" — a weak-signal cluster.`,
    "",
    "Documents:",
    docList + truncationNote,
    "",
    "Affirm verdict=yes only if the topic is clearly cross-cutting and the cluster has real cohesion (not a coincidence of keywords or tags). When uncertain, return verdict=no.",
    "",
    `Return JSON: { "verdict": "yes" | "no", "contextParagraph": "1-2 sentences describing the topic for the hub body (required if verdict=yes)" }`,
  ].join("\n");
}

function interpretCoherence(
  raw: unknown,
  isStrongSignal: boolean,
): HubCoherenceVerdict | null {
  if (typeof raw !== "object" || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const verdict = obj.verdict === "yes" ? "yes" : obj.verdict === "no" ? "no" : null;
  if (verdict === null) return null;
  if (isStrongSignal) {
    // Suppress-veto: explicit "no" suppresses; otherwise propose.
    if (verdict === "no") return null;
    const contextParagraph = typeof obj.contextParagraph === "string"
      ? obj.contextParagraph.trim() || undefined
      : undefined;
    return contextParagraph
      ? { verdict: "yes", contextParagraph }
      : { verdict: "yes" };
  }
  // Propose-veto (weak signal): explicit "yes" with context surfaces;
  // anything else suppresses.
  if (verdict !== "yes") return null;
  const contextParagraph = typeof obj.contextParagraph === "string"
    ? obj.contextParagraph.trim()
    : "";
  if (!contextParagraph) return null;
  return { verdict: "yes", contextParagraph };
}

function renderPreview(
  cluster: TopicCluster,
  contextParagraph: string | undefined,
): string {
  const lines: string[] = [
    `Proposed hub: hubs/_HUB-${cluster.topic}.md`,
    `Cluster size: ${cluster.docs.length} documents`,
  ];
  if (contextParagraph) {
    lines.push("", contextParagraph);
  }
  lines.push("", "Cross-linked docs:");
  for (const d of cluster.docs.slice(0, MAX_LLM_DOC_LIST)) {
    lines.push(`  - ${d.path}`);
  }
  if (cluster.docs.length > MAX_LLM_DOC_LIST) {
    lines.push(`  ... ${cluster.docs.length - MAX_LLM_DOC_LIST} more`);
  }
  return lines.join("\n");
}

function renderHubBody(
  cluster: TopicCluster,
  contextParagraph: string | undefined,
  now: Date,
): string {
  const ymd = formatYmd(now);
  const links = cluster.docs.map((d) => `- [[${stripMdExtension(d.path)}]]`).join("\n");
  const context = contextParagraph
    ? `${contextParagraph}\n\n`
    : "";
  return `---
type: hub
topic: ${cluster.topic}
created: ${ymd}
updated: ${ymd}
tags: [hub, proactive-curator]
---

# ${cluster.topic}

> Hub proposed by proactive-curator on ${ymd}. The user accepted this proposal; edit freely. The cross-linked source docs were not modified.

${context}## Cross-linked docs

${links}
`;
}

function stripMdExtension(p: string): string {
  return p.replace(/\.md$/i, "");
}

function formatYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

async function applyHubCreate(
  cwd: string,
  action: Extract<ProposalAction, { kind: "create-file" }>,
): Promise<string> {
  // hubs/ is a reserved system directory — by design the shared
  // validateDataRelativePath gate REJECTS it, so that gate cannot govern this
  // system-owned write. Containment is guaranteed structurally instead: the
  // folder is the fixed HUB_DIR constant, and the filename must be a plain
  // basename. The filename's slug derives from an LLM-clustered topic, so a
  // hostile topic like "../../x" must not be able to escape hubs/.
  if (action.folderPath !== HUB_DIR) {
    throw new Error(
      `Refusing to create hub outside ${HUB_DIR}/: got folderPath "${action.folderPath}".`,
    );
  }
  const fn = action.filename;
  // Reject path separators, traversal, control chars, and the Windows-reserved
  // filename characters — notably ":" which would create an NTFS alternate data
  // stream (`_HUB-C:evil.md` → file `_HUB-C` stream `evil.md`) rather than a
  // visible hub file. A denylist (not an ASCII allowlist) so non-Latin topic
  // names (e.g. a hub titled in a non-ASCII script, such as "_HUB-quarterly-review.md"
  // in another writing system) still pass — only the listed characters are rejected.
  // eslint-disable-next-line no-control-regex
  const unsafe = /[\u0000-\u001f<>:"/\\|?*]/;
  if (fn.trim().length === 0 || fn === "." || fn === ".." || unsafe.test(fn)) {
    throw new Error(
      `Refusing to create hub: unsafe filename "${action.filename}" — must be a plain ` +
        'basename with no path separators, traversal, or reserved characters (including ":").',
    );
  }
  const folder = join(cwd, "data", HUB_DIR);
  await mkdir(folder, { recursive: true });
  const file = join(folder, fn);
  // Exclusive create — never clobber an existing hub (e.g. a TOCTOU between the
  // pickCluster existence check and here). Surfaces a clear "refusing to
  // overwrite" error instead of silent data loss.
  await exclusiveCreateFile(file, action.body);
  return file;
}
